/**
 * Honest labelling of recorded spend actors. Pure, so it unit-tests under the `node`
 * vitest environment.
 *
 * The U6 endpoints pass every `userId` through exactly as recorded — lump sentinels and
 * gateway service-principal actor ids included — because translating them server-side
 * would let a pre-migration lump row masquerade as attributed spend. Bucketing them is
 * this module's job.
 *
 * The classification is deliberately FAIL-SAFE: a row is treated as a person only when
 * it is positively identifiable as one, and everything else lands in the unattributed
 * bucket. The alternative default (unknown ⇒ person) is the failure the plan forbids —
 * a service actor or a pre-migration lump silently sitting in the human user table. No
 * spend is hidden either way: the unattributed bucket renders its own rows and total
 * alongside the user rows, and every row keeps its raw id.
 *
 * Two id shapes are known to be people:
 *   - `acc_*` — the canonical Parslee account id (CanonicalAccountId in m365dotnet).
 *   - an email — a legacy pre-PAR-6160 sender. The gateway resolver explicitly accepts
 *     and counts these rather than dropping them, so they must not be buried either.
 */
import type { UsageByUserTotals } from '@/services/adminApi';

/** The lump-sum sentinel salestrainer wrote before the detailed-event migration. */
export const LUMP_SENTINEL_USER_ID = 'salestrainer-system';

export type UnattributedReason = 'lump' | 'service' | 'unknown';

export type ActorClassification =
  | { kind: 'user' }
  | { kind: 'unattributed'; reason: UnattributedReason };

const ACCOUNT_ID_PREFIX = 'acc_';

/** Entra app ids (azp / appid / client_id) are GUIDs, with or without dashes. */
const GUID = /^\{?[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}\}?$/i;

// Deliberately loose: this only has to recognise the legacy email-shaped ids the gateway
// already logs as email-shaped, not validate deliverability.
const EMAIL_SHAPED = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function classifyUsageActor(userId: string): ActorClassification {
  const id = userId.trim();

  if (id === LUMP_SENTINEL_USER_ID) return { kind: 'unattributed', reason: 'lump' };
  if (id.startsWith(ACCOUNT_ID_PREFIX) && id.length > ACCOUNT_ID_PREFIX.length) {
    return { kind: 'user' };
  }
  if (EMAIL_SHAPED.test(id)) return { kind: 'user' };
  if (GUID.test(id)) return { kind: 'unattributed', reason: 'service' };
  return { kind: 'unattributed', reason: 'unknown' };
}

export const UNATTRIBUTED_REASON_LABELS: Record<UnattributedReason, string> = {
  lump: 'Pre-migration lump sum',
  service: 'Service credential (no end user)',
  unknown: 'Unrecognised actor id',
};

export interface PartitionedUsers {
  /** Rows positively identified as people, descending by total tokens. */
  users: UsageByUserTotals[];
  /** Sentinel / service / unrecognised rows, descending by total tokens. */
  unattributed: UsageByUserTotals[];
  userTokens: number;
  unattributedTokens: number;
  /** Sum over BOTH buckets — must equal the endpoint's own totalTokens. */
  totalTokens: number;
}

const byTokensDesc = (a: UsageByUserTotals, b: UsageByUserTotals) => b.totalTokens - a.totalTokens;

/**
 * Split by-user rows into people and the unattributed bucket. The two buckets are never
 * merged and never dropped, so their token sums always add back to the endpoint total.
 */
export function partitionUsers(rows: UsageByUserTotals[]): PartitionedUsers {
  const users: UsageByUserTotals[] = [];
  const unattributed: UsageByUserTotals[] = [];

  for (const row of rows) {
    if (classifyUsageActor(row.userId).kind === 'user') users.push(row);
    else unattributed.push(row);
  }

  const sum = (rs: UsageByUserTotals[]) => rs.reduce((t, r) => t + r.totalTokens, 0);
  const userTokens = sum(users);
  const unattributedTokens = sum(unattributed);

  return {
    users: users.sort(byTokensDesc),
    unattributed: unattributed.sort(byTokensDesc),
    userTokens,
    unattributedTokens,
    totalTokens: userTokens + unattributedTokens,
  };
}

/**
 * Display form for an id we cannot resolve to a name — the dashboard has no directory
 * lookup, so every id renders opaque. Long ids are middle-truncated for layout only;
 * callers pass the full value as a `title` so nothing is lost.
 */
export function formatOpaqueId(id: string, maxLength = 28): string {
  if (id.length <= maxLength) return id;
  const head = Math.ceil((maxLength - 1) / 2);
  const tail = Math.floor((maxLength - 1) / 2);
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}

/** Action rows arrive with a null name when the recording sink never set one. */
export function formatActionName(actionName: string | null): string {
  return actionName ?? 'No action recorded';
}
